import { Link } from "react-router-dom";
import { ArrowRight, Building2, CheckCircle2, ClipboardCheck, Ruler, Sun, Wrench } from "lucide-react";
import { site } from "@/config/site";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd, faqLd, localBusinessLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { FAQ } from "@/components/site/FAQ";
import { CTASection } from "@/components/site/CTASection";

const sectors = [
  "Offices", "Shops", "Restaurants", "Cafés", "Salons", "Clinics",
  "Landlords", "Property managers", "Developers", "Schools", "Commercial properties",
];

const points = [
  { icon: Sun, title: "Glare & privacy control", text: "Vertical, roller and venetian ranges that tame screen glare and street-level privacy without turning rooms dark." },
  { icon: Building2, title: "Durable commercial ranges", text: "Hard-wearing, easy-clean fabrics and mechanisms specified for heavier daily use in workplaces." },
  { icon: Ruler, title: "Measured to your premises", text: "Every window measured on site — large glazed fronts, odd shapes and multi-window runs included." },
  { icon: Wrench, title: "Fitted with minimal disruption", text: "Tidy, professional installation planned around your opening hours wherever possible." },
];

const steps = [
  { icon: ClipboardCheck, title: "Request a site visit", text: "Tell us about your premises and we'll arrange a convenient time to come to you." },
  { icon: Ruler, title: "On-site measure & advice", text: "We measure every window and recommend durable, practical options for your space and budget." },
  { icon: Building2, title: "Clear written quotation", text: "One straightforward quote for supply and professional fitting — no hidden extras." },
  { icon: Wrench, title: "Professional installation", text: "Fitted by our own team, checked, and left clean and ready to use." },
];

const faqs = [
  {
    q: "Which blinds work best in offices?",
    a: "Vertical and roller blinds are the office staples — excellent glare control for screens and easy to maintain. For meeting rooms and street-facing glass, venetian or day & night blinds add flexible privacy.",
  },
  {
    q: "Can you fit outside opening hours?",
    a: "Yes — we plan commercial installations around your trading hours wherever possible, including early mornings and Saturdays by arrangement.",
  },
  {
    q: "Do you work with landlords and letting agents?",
    a: "Regularly. We supply durable, cost-effective blinds for rental properties across Birmingham, with straightforward pricing for single properties or portfolios.",
  },
  {
    q: "Is the commercial site visit free?",
    a: "Yes — the visit, measuring and written quotation are free and without obligation, for commercial premises just as for homes.",
  },
];

export default function CommercialBlinds() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Commercial Blinds Birmingham | Office & Business Blinds | My Blinds Company"
        description="Commercial blinds in Birmingham & the West Midlands for offices, shops, restaurants, salons, clinics and landlords. Free site visit, measuring and professional fitting."
        path="/commercial-blinds-birmingham"
        jsonLd={[
          localBusinessLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Commercial Blinds Birmingham", path: "/commercial-blinds-birmingham" },
          ]),
          faqLd(faqs),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Commercial blinds supply and installation",
            provider: { "@type": "LocalBusiness", name: "My Blinds Company Ltd" },
            areaServed: "Birmingham & West Midlands, UK",
          },
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-950 text-white overflow-hidden grain" data-testid="commercial-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <Reveal className="max-w-3xl">
            <Eyebrow className="text-white/60">For Business</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
              Commercial Blinds in Birmingham &amp; the West Midlands
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/75 max-w-2xl">
              Durable, made-to-measure blinds for workplaces of every kind —
              measured on site, clearly quoted and professionally fitted around
              your opening hours.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="commercial-site-visit-btn"
                onClick={() => openForm({ source: "commercial_page", productInterest: "Commercial Blinds" })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-navy-900 shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
              >
                Request a Commercial Site Visit <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href={site.phone.href}
                data-testid="commercial-call-btn"
                className="inline-flex items-center justify-center rounded-full border border-white/40 bg-white/5 backdrop-blur px-8 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-navy-900"
              >
                Call {site.phone.display}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-16 sm:py-24" data-testid="commercial-sectors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-10">
            <Eyebrow>Who We Work With</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Blinds for every kind of premises
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {sectors.map((s) => (
              <span key={s} className="rounded-full border border-warm-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm">
                {s}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {points.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-warm-200 bg-white p-7 shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy-900 mb-5">
                    <p.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-sans text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 sm:py-24 bg-warm-100/60" data-testid="commercial-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              From site visit to finished fitting
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-warm-200 bg-white p-6 shadow-sm h-full">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-navy-900 mb-4">
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-sans text-base font-semibold text-slate-900 mb-1.5">{s.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl">
              {["Free site visit, measuring & quotation", "Flame-retardant and easy-clean options available", "Planned around your opening hours", "One point of contact from quote to fitting"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight">
              Commercial blinds FAQs
            </h2>
          </Reveal>
          <FAQ items={faqs} idPrefix="commercial-faq" />
          <Reveal className="mt-10 text-center text-sm text-slate-600">
            Looking for your home instead? See our <Link to="/blinds" className="font-semibold text-navy-900 hover:underline">full blinds range</Link> or{" "}
            <Link to="/blinds-birmingham" className="font-semibold text-navy-900 hover:underline">blinds in Birmingham</Link>.
          </Reveal>
        </div>
      </section>

      <CTASection source="commercial_final" />
    </>
  );
}
